import {
  activityEntriesForDisplay,
  activityEntriesFromEvents,
  assistantTextFromEvents,
  canCancel,
  failureSummaryFromTurn,
  runReadableResult,
  threadSummaryFromEvents,
  threadWarningFromRun
} from "../domain/conversation.js";
import { renderActivityTimeline } from "../render/activity-timeline.js";
import { renderTurnArtifactBody } from "../domain/turn-detail.js";
import { renderDecisionStack } from "../render/decision-cards.js";
import {
  escapeHTML,
  formatBytes,
  formatTimestamp,
  renderContextRow,
  renderContextStrip,
  renderEmptyState,
  renderMessageContent,
  renderSkeleton,
  renderTabs,
  renderTruncatedID
} from "../render/ui-helpers.js";
import { selectedTurnArtifact } from "./state.js";

export function renderTurnDetailComponent(state, options = {}) {
  if (state.notFound) {
    return renderEmptyState(
      "Turn not found",
      "The selected turn no longer exists.",
      `<div class="button-row"><a class="button button--primary" href="${escapeHTML(options.backHref || "/turns")}" data-route>Back to diagnostics</a></div>`
    );
  }
  if (!state.turn) {
    return renderSkeleton(5);
  }

  const item = state.turn;
  const liveAssistantText = assistantTextFromEvents(state.events);
  const readableResult = runReadableResult(item, state.events);
  const completedSummary = threadSummaryFromEvents(state.events);

  return `
    <section class="stack" data-turn-detail-component>
      <div style="display:flex; justify-content:space-between; align-items:center;">
        <div class="button-row">
          <span class="badge badge--${escapeHTML(item.state)}">${escapeHTML(item.state)}</span>
          ${renderTruncatedID(item.id)}
        </div>
        <div class="button-row">
          <button class="button button--sm button--ghost" type="button" data-action="toggle-detail-drawer">Details</button>
          <button class="button button--sm button--danger" type="button" data-action="cancel-run" ${canCancel(item.state) ? "" : "disabled"}>Cancel</button>
        </div>
      </div>
      ${renderContextStrip([
    { label: "Agent", value: lookupItemName(options.agents, item.agent_id) },
    { label: "Workspace", value: lookupItemName(options.workspaces, item.workspace_id) },
    { label: "Base", value: item.base_ref || "auto" },
    { label: "Branch", value: item.branch_name || "auto" },
    { label: "Stream", value: state.streamState }
  ])}
      ${renderTabs([
    { id: "output", label: "Output" },
    { id: "events", label: "Events", count: state.events.length },
    { id: "artifacts", label: "Artifacts", count: state.artifacts.length }
  ], state.activeTab)}
      ${state.activeTab === "output" ? renderTurnOutputTab(state, item, liveAssistantText, readableResult, completedSummary, options) : ""}
      ${state.activeTab === "events" ? renderTurnEventsTab(state) : ""}
      ${state.activeTab === "artifacts" ? renderTurnArtifactsTab(state) : ""}
    </section>
  `;
}

export function renderTurnDetailDrawer(state, options = {}) {
  const turnItem = state.turn;
  if (!turnItem) {
    return "";
  }
  const workspaceDiagnostics = options.workspaceDiagnostics || null;
  const turnDiagnostics = options.turnDiagnostics || null;
  return `
    <div class="detail-drawer-overlay" data-action="close-drawer"></div>
    <aside class="detail-drawer">
      <button class="detail-drawer__close" data-action="close-drawer">X</button>
      <div class="panel-eyebrow">Turn context</div>
      <div class="list">
        ${renderContextRow("Turn ID", turnItem.id)}
        ${renderContextRow("Thread", turnItem.thread_id || "standalone")}
        ${renderContextRow("Workspace repo", workspaceDiagnostics?.repo_path || "n/a")}
        ${renderContextRow("Wait state", turnItem.wait_state || "none")}
        ${renderContextRow("Worktree", turnDiagnostics?.worktree_path || "n/a")}
        ${turnDiagnostics?.stream?.phase ? renderContextRow("Stream phase", turnDiagnostics.stream.phase.replaceAll("_", " ")) : ""}
        ${turnDiagnostics?.stream?.request_kind ? renderContextRow("Request", turnDiagnostics.stream.request_kind.replaceAll("_", " ")) : ""}
        ${turnDiagnostics?.stream?.last_provider_event_at ? renderContextRow("Last provider event", formatTimestamp(turnDiagnostics.stream.last_provider_event_at)) : ""}
        ${turnDiagnostics?.provider_trace?.retained ? renderContextRow("Provider trace", `${turnDiagnostics.provider_trace.event_count} retained (${turnDiagnostics.provider_trace.retention_reason || "diagnostics"})`) : renderContextRow("Provider trace", "not retained")}
        ${turnDiagnostics?.managed_harness_log_path ? renderContextRow("Managed harness log", turnDiagnostics.managed_harness_log_path) : ""}
        ${renderContextRow("Commit before", turnItem.head_commit_before || "n/a")}
        ${renderContextRow("Commit after", turnItem.head_commit_after || "n/a")}
        ${renderContextRow("Started", formatTimestamp(turnItem.started_at))}
        ${renderContextRow("Finished", formatTimestamp(turnItem.finished_at))}
        ${renderContextRow("Created", formatTimestamp(turnItem.created_at))}
        ${turnItem.error_message ? renderContextRow(turnItem.state === "applied_with_warning" ? "Warning" : "Error", turnItem.error_message) : ""}
      </div>
    </aside>
  `;
}

function renderTurnOutputTab(state, item, liveAssistantText, readableResult, completedSummary, options = {}) {
  const waitingOnDecision = item.wait_state === "awaiting_decision";
  const warningText = threadWarningFromRun(item, state.events);
  const failureSummary = failureSummaryFromTurn(item, state.events, options.turnDiagnostics);
  const displayActivity = activityEntriesForDisplay(state.events, item);
  const diagnosticActivity = activityEntriesFromEvents(state.events, item).filter((e) => e.tier === 3);
  const bubbleModifier = item.state === "applied_with_warning" ? "message-bubble--warning" : "message-bubble--agent";
  return `
    <article class="panel">
      <div class="panel-eyebrow">Prompt</div>
      <pre class="code-block">${escapeHTML(item.prompt || "")}</pre>
    </article>
    <article class="panel">
      <div class="panel-eyebrow">Response ${item.state === "running" ? "(streaming)" : ""}</div>
      <div class="message-bubble ${bubbleModifier}" style="max-width:100%">
        <div class="message-bubble__body">${renderMessageContent(liveAssistantText || readableResult || "Waiting for output...")}${item.state === "running" && !waitingOnDecision ? `<span class="stream-cursor" aria-hidden="true">&#9613;</span>` : ""}</div>
      </div>
      ${warningText ? `<div class="form-note form-note--warn" style="margin-top:var(--space-sm)">${escapeHTML(warningText)}</div>` : ""}
      ${completedSummary && completedSummary !== readableResult ? `<div class="form-note" style="margin-top:var(--space-sm)">Summary: ${escapeHTML(completedSummary)}</div>` : ""}
    </article>
    ${displayActivity.length ? `
      <article class="panel">
        <div class="panel-header panel-header--compact">
          <div>
            <div class="panel-eyebrow">Activity timeline</div>
            <div class="panel-copy">Tool calls, decisions, and progress as they happen.</div>
          </div>
        </div>
        ${renderActivityTimeline(displayActivity, { live: item.state === "running" && !waitingOnDecision })}
      </article>
    ` : ""}
    ${diagnosticActivity.length ? `
      <article class="panel">
        <div class="panel-header panel-header--compact">
          <div>
            <div class="panel-eyebrow">Diagnostics</div>
            <div class="panel-copy">Stream lifecycle and internal events.</div>
          </div>
        </div>
        <div class="list">
          ${diagnosticActivity.map((e) => `<div class="context-row"><span class="context-row__label">${escapeHTML(e.title || e.eventType)}</span><span class="context-row__value">${escapeHTML(e.detail || "")}</span></div>`).join("")}
        </div>
      </article>
    ` : ""}
    ${renderTurnFailureCallout(item, failureSummary, options.turnDiagnostics)}
    ${state.decisions.length ? `
      <article class="panel">
        <div class="panel-header panel-header--compact">
          <div>
            <div class="panel-eyebrow">Operator decisions</div>
            <div class="panel-copy">Human input requested during this turn.</div>
          </div>
        </div>
        ${renderDecisionStack(state.decisions, {
    compact: false,
    decisionDrafts: options.decisionDrafts,
    escapeHTML,
    formatTimestamp,
    lookupTurn: options.lookupTurn,
    threadID: item.thread_id,
    turnID: item.id
  })}
      </article>
    ` : ""}
  `;
}

function renderTurnFailureCallout(item, failureSummary, turnDiagnostics) {
  const isFailureLike = item.state === "failed" || item.state === "canceled" || item.state === "applied_with_warning";
  const providerTrace = turnDiagnostics?.provider_trace || null;
  if (!isFailureLike && !providerTrace?.retained) {
    return "";
  }
  return `
    <article class="panel">
      <div class="panel-header panel-header--compact">
        <div>
          <div class="panel-eyebrow">${isFailureLike ? "Failure analysis" : "Diagnostics"}</div>
          <div class="panel-copy">${escapeHTML(failureSummary || "This turn retained additional diagnostics for inspection.")}</div>
        </div>
      </div>
      <div class="list">
        ${turnDiagnostics?.stream?.failure_phase ? renderContextRow("Failure phase", turnDiagnostics.stream.failure_phase.replaceAll("_", " ")) : ""}
        ${turnDiagnostics?.stream?.phase ? renderContextRow("Observed phase", turnDiagnostics.stream.phase.replaceAll("_", " ")) : ""}
        ${turnDiagnostics?.stream?.last_provider_event_at ? renderContextRow("Last provider event", formatTimestamp(turnDiagnostics.stream.last_provider_event_at)) : ""}
        ${providerTrace?.retained ? renderContextRow("Provider trace", `${providerTrace.event_count} retained (${providerTrace.retention_reason || "diagnostics"})`) : ""}
      </div>
    </article>
  `;
}


function renderTurnEventsTab(state) {
  if (state.events.length === 0) {
    return renderEmptyState("No events", "Events will appear here as the turn executes.");
  }
  return `
    <div class="scroll-pane">
      ${state.events.map(renderEventCard).join("")}
    </div>
  `;
}

function renderTurnArtifactsTab(state) {
  const artifact = selectedTurnArtifact(state);
  if (state.artifacts.length === 0) {
    return renderEmptyState("No artifacts", "Diffs and changed files will appear here after the turn completes.");
  }
  return `
    <div class="list">
      ${state.artifacts.map((item) => renderArtifactCard(state, item)).join("")}
    </div>
    ${artifact ? `
      <article class="panel">
        <div class="panel-header">
          <div>
            <div class="panel-eyebrow">Inspector</div>
            <div class="panel-title">${escapeHTML(artifact.path || artifact.kind)}</div>
            <div class="panel-copy">${escapeHTML(artifact.kind)} / ${escapeHTML(artifact.content_type || "application/octet-stream")} / ${escapeHTML(formatBytes(artifact.bytes))}</div>
          </div>
          <button class="button button--sm button--ghost" type="button" data-action="load-artifact" ${artifact.storage_path && isTurnTextContent(artifact.content_type) ? "" : "disabled"}>Load content</button>
        </div>
        <pre class="code-block">${escapeHTML(renderTurnArtifactBody(state))}</pre>
      </article>
    ` : ""}
  `;
}

function renderEventCard(item) {
  return `
    <details class="event-card disclosure">
      <summary>
        <span style="font-weight:700">${escapeHTML(item.event_type)}</span>
        <span class="mini-label" style="margin-left:var(--space-sm)">seq ${escapeHTML(String(item.seq))}</span>
        <span class="truncated-id" style="margin-left:var(--space-sm)">${escapeHTML(formatTimestamp(item.created_at))}</span>
      </summary>
      <pre class="code-block" style="margin-top:var(--space-sm)">${escapeHTML(formatTurnPayload(item.payload_json))}</pre>
    </details>
  `;
}

function renderArtifactCard(state, item) {
  const selected = state.selectedArtifactID === item.id;
  return `
    <article class="artifact-card ${selected ? "artifact-card--selected" : ""}" data-artifact-id="${escapeHTML(item.id)}">
      <div class="artifact-card__main">
        <div>
          <div class="artifact-card__title">${escapeHTML(item.path || item.kind)}</div>
          <div class="artifact-card__meta">${escapeHTML(item.kind)} / ${escapeHTML(formatBytes(item.bytes))}</div>
        </div>
        <span class="badge">${escapeHTML(formatBytes(item.bytes))}</span>
      </div>
    </article>
  `;
}

function lookupByID(items, id) {
  return (items || []).find((item) => item.id === id) || null;
}

function lookupItemName(items, id) {
  const match = lookupByID(items, id);
  return match ? match.name : id || "unknown";
}

function formatTurnPayload(raw) {
  try {
    return JSON.stringify(JSON.parse(raw || "{}"), null, 2);
  } catch (_error) {
    return raw || "{}";
  }
}

function isTurnTextContent(contentType) {
  const lowered = String(contentType || "").toLowerCase();
  return lowered.startsWith("text/") ||
    lowered.includes("json") ||
    lowered.includes("xml") ||
    lowered.includes("diff") ||
    lowered.includes("deleted");
}
